import { createContext, useContext, useState, ReactNode, useEffect } from 'react'

interface SearchFiltersContextType {
  activeFilters: any[]
  setActiveFilters: (filters: any[]) => void
  filteredPmids: Set<string>
  setFilteredPmids: (pmids: Set<string>) => void
  iciteStatus: 'idle' | 'fetching' | 'done' | 'error'
  setIciteStatus: (status: 'idle' | 'fetching' | 'done' | 'error') => void
  iciteMessage: string
  setIciteMessage: (msg: string) => void
  iciteFetchTime: number
  setIciteFetchTime: (time: number) => void
  datasetAnalysis: any | null
  setDatasetAnalysis: (analysis: any | null) => void
  datasetTopicNames: Record<string, string>
  setDatasetTopicNames: (names: Record<string, string>) => void
  enhancedTopicAnnotations: boolean
  setEnhancedTopicAnnotations: (enabled: boolean) => void
  currentAnalysis: any | null
  setCurrentAnalysis: (analysis: any | null) => void
  currentTopicNames: Record<string, string>
  setCurrentTopicNames: (names: Record<string, string>) => void
  loadedDatasetId: string | null
  setLoadedDatasetId: (id: string | null) => void
}

const SearchFiltersContext = createContext<SearchFiltersContextType | null>(null)

const STORAGE_KEY = 'pubmed_search_filters'

function loadPersistedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        currentAnalysis: parsed.currentAnalysis || null,
        currentTopicNames: parsed.currentTopicNames || {},
      }
    }
  } catch (e) {
    console.error('Failed to load persisted search filters:', e)
  }
  return { currentAnalysis: null, currentTopicNames: {} }
}

export function SearchFiltersProvider({ children }: { children: ReactNode }) {
  const persisted = loadPersistedState()
  
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [filteredPmids, setFilteredPmids] = useState<Set<string>>(new Set())
  const [iciteStatus, setIciteStatus] = useState<'idle' | 'fetching' | 'done' | 'error'>('idle')
  const [iciteMessage, setIciteMessage] = useState('')
  const [iciteFetchTime, setIciteFetchTime] = useState(0)
  const [datasetAnalysis, setDatasetAnalysis] = useState<any | null>(null)
  const [datasetTopicNames, setDatasetTopicNames] = useState<Record<string, string>>({})
  const [enhancedTopicAnnotations, setEnhancedTopicAnnotations] = useState(true)
  const [currentAnalysis, setCurrentAnalysis] = useState<any | null>(persisted.currentAnalysis)
  const [currentTopicNames, setCurrentTopicNames] = useState<Record<string, string>>(persisted.currentTopicNames)
  const [loadedDatasetId, setLoadedDatasetId] = useState<string | null>(null)

  // Persist analysis state to localStorage. Big BERTopic results
  // (10k+ docs) can blow past the ~5 MB quota; that throw used to
  // crash the whole React tree and white-page the app on every
  // subsequent load. Now: try the full payload first; on quota error
  // (or just preemptively if the JSON is large) persist topic names
  // only and drop the heavy analysis blob. The user can still re-load
  // it from the Jobs panel.
  useEffect(() => {
    const trySet = (payload: any): boolean => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
        return true
      } catch {
        return false
      }
    }

    const full = { currentAnalysis, currentTopicNames }
    let json: string
    try {
      json = JSON.stringify(full)
    } catch {
      // Circular ref or weird payload — give up cleanly.
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
      return
    }
    // 1 MB cap leaves headroom for other apps on the same origin.
    if (json.length <= 1024 * 1024 && trySet(full)) return

    // Too big or quota refused — keep just the topic-name labels.
    if (!trySet({ currentAnalysis: null, currentTopicNames })) {
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
    }
  }, [currentAnalysis, currentTopicNames])

  return (
    <SearchFiltersContext.Provider value={{
      activeFilters, setActiveFilters,
      filteredPmids, setFilteredPmids,
      iciteStatus, setIciteStatus,
      iciteMessage, setIciteMessage,
      iciteFetchTime, setIciteFetchTime,
      datasetAnalysis, setDatasetAnalysis,
      datasetTopicNames, setDatasetTopicNames,
      enhancedTopicAnnotations, setEnhancedTopicAnnotations,
      currentAnalysis, setCurrentAnalysis,
      currentTopicNames, setCurrentTopicNames,
      loadedDatasetId, setLoadedDatasetId,
    }}>
      {children}
    </SearchFiltersContext.Provider>
  )
}

export function useSearchFilters() {
  const context = useContext(SearchFiltersContext)
  if (!context) throw new Error('useSearchFilters must be used within SearchFiltersProvider')
  return context
}
